import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, Search, ChevronLeft, FileText, Radio } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const assetUrl = (u) => (u ? `${API_BASE}${u}` : null);
const GRADIENTS = [
  "linear-gradient(135deg,#723FFF,#B79CFF)", "linear-gradient(135deg,#0EA5E9,#6EE7F9)",
  "linear-gradient(135deg,#F59E0B,#FCD34D)", "linear-gradient(135deg,#EC4899,#FBCFE8)",
  "linear-gradient(135deg,#10B981,#6EE7B7)", "linear-gradient(135deg,#6366F1,#A5B4FC)",
];
const grad = (id) => GRADIENTS[(id || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0) % GRADIENTS.length];

export const CLIENT_KB_API = { home: "/kb", group: (id) => `/kb/groups/${id}`, category: (id) => `/kb/categories/${id}`, article: (id) => `/kb/articles/${id}`, search: "/kb/search" };
export const CONSOLE_KB_API = { home: "/console/kb/read", group: (id) => `/console/kb/read/groups/${id}`, category: (id) => `/console/kb/read/categories/${id}`, article: (id) => `/console/kb/read/articles/${id}`, search: "/console/kb/search" };

function Card({ testid, title, subtitle, imageUrl, seed, onClick }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className="group text-left rounded-2xl overflow-hidden border border-[#EEEFEF] bg-white premium-shadow hover:-translate-y-1 hover:shadow-lg transition-[transform,box-shadow] duration-200">
      <div className="h-32 w-full bg-cover bg-center" style={imageUrl ? { backgroundImage: `url(${imageUrl})` } : { backgroundImage: grad(seed) }} />
      <div className="p-4">
        <h3 className="font-display text-base font-semibold text-[#06060C] leading-snug line-clamp-2 group-hover:text-[#5b2fd6] transition-colors">{title}</h3>
        {subtitle && <p className="text-xs text-[#9FA1A4] mt-1">{subtitle}</p>}
      </div>
    </button>
  );
}

function Crumbs({ base, group, category, article, go }) {
  const parent = category?.parent;
  return (
    <div className="mt-8 flex flex-wrap items-center gap-2 text-sm" data-testid="kb-breadcrumb">
      <button onClick={() => go(base)} data-testid="kb-crumb-home" className="text-[#5b2fd6] hover:underline">All topics</button>
      {group && <><span className="text-[#c4c5cc]">/</span><button data-testid="kb-crumb-group" onClick={() => go(`${base}/${group.id}`)} className={category ? "text-[#5b2fd6] hover:underline" : "text-[#06060C]"}>{group.name}</button></>}
      {parent && <><span className="text-[#c4c5cc]">/</span><button data-testid="kb-crumb-parent" onClick={() => go(`${base}/${group.id}/${parent.id}`)} className="text-[#5b2fd6] hover:underline">{parent.name}</button></>}
      {category && <><span className="text-[#c4c5cc]">/</span><button data-testid="kb-crumb-category" onClick={() => go(`${base}/${group.id}/${category.id}`)} className={article ? "text-[#5b2fd6] hover:underline" : "text-[#06060C]"}>{category.name}</button></>}
      {article && <><span className="text-[#c4c5cc]">/</span><span className="text-[#06060C] line-clamp-1">{article.title}</span></>}
    </div>
  );
}

export function KbReader({ basePath, endpoints, accent = "#723FFF", headerSlot }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [gid, cid, aid] = pathname.startsWith(basePath) ? pathname.slice(basePath.length).split("/").filter(Boolean) : [];
  const [home, setHome] = useState(null);
  const [group, setGroup] = useState(null);
  const [cat, setCat] = useState(null);
  const [article, setArticle] = useState(null);
  const [missing, setMissing] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const go = (to) => { setQ(""); setResults(null); navigate(to); };

  useEffect(() => { api.get(endpoints.home).then(({ data }) => setHome(data)).catch(() => setHome({ groups: [] })); }, [endpoints.home]);
  useEffect(() => { setMissing(false); setGroup(null); if (gid) api.get(endpoints.group(gid)).then(({ data }) => setGroup({ ...data.group, categories: data.categories })).catch(() => setMissing(true)); }, [gid, endpoints]);
  useEffect(() => { setCat(null); if (cid) api.get(endpoints.category(cid)).then(({ data }) => setCat({ ...data.category, articles: data.articles, subcategories: data.subcategories || [] })).catch(() => setMissing(true)); }, [cid, endpoints]);
  useEffect(() => { setArticle(null); if (aid) api.get(endpoints.article(aid)).then(({ data }) => setArticle(data)).catch(() => setMissing(true)); }, [aid, endpoints]);
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResults(null); return undefined; }
    const t = setTimeout(() => api.get(`${endpoints.search}?q=${encodeURIComponent(term)}`).then(({ data }) => setResults(data.results || [])).catch(() => setResults([])), 300);
    return () => clearTimeout(t);
  }, [q, endpoints.search]);

  if (!home) return <div className="flex justify-center py-32"><Loader2 className="h-6 w-6 animate-spin text-[#c4c5cc]" /></div>;
  const busy = (gid && !group) || (cid && !cat) || (aid && !article);

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-8 py-8" data-testid="knowledge-base-page">
      <div className="rounded-3xl p-8 md:p-12 text-white relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${accent}, ${accent}bb)` }}>
        <div className="relative z-10">
          <div className="flex items-start justify-between gap-4">
            <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight" data-testid="kb-greeting">Welcome {home.greeting_name}. How can we help?</h1>
            {headerSlot}
          </div>
          <div className="mt-6 max-w-xl relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#9FA1A4]" />
            <input data-testid="kb-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search articles…"
              className="w-full h-12 rounded-full bg-white pl-12 pr-4 text-[#06060C] text-sm outline-none shadow-lg placeholder:text-[#9FA1A4]" />
          </div>
        </div>
        <div className="absolute -right-10 -bottom-10 h-48 w-48 rounded-full bg-white/10" />
      </div>

      {results !== null ? (
        <div className="mt-8" data-testid="kb-search-results">
          <p className="font-mono-data text-[10px] uppercase tracking-wider text-[#9FA1A4] mb-3">{results.length} result{results.length !== 1 ? "s" : ""}</p>
          {results.length === 0 ? <p className="text-sm text-[#9FA1A4]">No articles matched “{q}”.</p> : (
            <div className="space-y-2">
              {results.map((r) => (
                <button key={r.id} data-testid={`kb-result-${r.id}`} onClick={() => go(`${basePath}/${r.group_id}/${r.category_id}/${r.id}`)}
                  className="w-full text-left rounded-xl border border-[#EEEFEF] bg-white px-4 py-3 hover:border-[#d9ccff] transition-colors">
                  <div className="flex items-center gap-2"><FileText className="h-4 w-4 text-[#5b2fd6] shrink-0" /><span className="font-medium text-[#06060C] text-sm">{r.title}</span>{r.group_name && <span className="font-mono-data text-[10px] text-[#9FA1A4]">· {r.group_name}</span>}</div>
                  {r.snippet && <p className="text-xs text-[#9FA1A4] mt-1 line-clamp-1 pl-6">…{r.snippet}…</p>}
                </button>
              ))}
            </div>
          )}
        </div>
      ) : missing ? (
        <div className="mt-16 text-center text-sm text-[#9FA1A4]" data-testid="kb-not-found">This page isn't available. <button onClick={() => go(basePath)} className="text-[#5b2fd6] hover:underline">Back to all topics</button></div>
      ) : busy ? (
        <div className="flex justify-center py-20"><Loader2 className="h-5 w-5 animate-spin text-[#c4c5cc]" /></div>
      ) : !gid ? (
        home.groups.length === 0 ? (
          <div className="mt-16 text-center" data-testid="kb-empty-state">
            <div className="mx-auto h-16 w-16 rounded-2xl bg-[#F6F6FD] flex items-center justify-center"><Radio className="h-7 w-7 text-[#c4b5ff] animate-pulse" /></div>
            <p className="mt-4 font-display text-lg text-[#06060C]">Knowledgebase transmission in transit.</p>
            <p className="text-sm text-[#9FA1A4]">Come back later.</p>
          </div>
        ) : (
          <div className="mt-8">
            <p className="font-mono-data text-[10px] uppercase tracking-wider text-[#9FA1A4] mb-4">Browse topics</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {home.groups.map((g) => <Card key={g.id} testid={`kb-group-${g.id}`} title={g.name} subtitle={g.description} imageUrl={assetUrl(g.image_url)} seed={g.id} onClick={() => go(`${basePath}/${g.id}`)} />)}
            </div>
          </div>
        )
      ) : (
        <>
          <Crumbs base={basePath} group={group} category={cat} article={article} go={go} />
          {!cid && (
            <div className="mt-6">
              {group.categories.length === 0 ? <p className="text-sm text-[#9FA1A4]">No categories here yet.</p> : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {group.categories.map((c) => <Card key={c.id} testid={`kb-category-${c.id}`} title={c.name} subtitle={c.subcategory_count ? `${c.subcategory_count} sub-categor${c.subcategory_count !== 1 ? "ies" : "y"} · ${c.article_count} article${c.article_count !== 1 ? "s" : ""}` : `${c.article_count} article${c.article_count !== 1 ? "s" : ""}`} imageUrl={assetUrl(c.image_url)} seed={c.id} onClick={() => go(`${basePath}/${gid}/${c.id}`)} />)}
                </div>
              )}
            </div>
          )}
          {cid && !aid && cat.subcategories.length > 0 && (
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="kb-subcategories">
              {cat.subcategories.map((c) => <Card key={c.id} testid={`kb-subcategory-${c.id}`} title={c.name} subtitle={`${c.article_count} article${c.article_count !== 1 ? "s" : ""}`} imageUrl={assetUrl(c.image_url)} seed={c.id} onClick={() => go(`${basePath}/${gid}/${c.id}`)} />)}
            </div>
          )}
          {cid && !aid && cat.subcategories.length === 0 && (
            <div className="mt-6">
              {cat.articles.length === 0 ? <p className="text-sm text-[#9FA1A4]">No articles in this category yet.</p> : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {cat.articles.map((a) => (
                    <button key={a.id} data-testid={`kb-article-${a.id}`} onClick={() => go(`${basePath}/${gid}/${cid}/${a.id}`)}
                      className="text-left rounded-xl border border-[#EEEFEF] bg-white px-4 py-4 hover:border-[#d9ccff] hover:-translate-y-0.5 transition-[transform,border-color] flex items-center gap-3">
                      <FileText className="h-4 w-4 text-[#5b2fd6] shrink-0" /><span className="font-medium text-[#06060C] text-sm">{a.title}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {aid && (
            <article className="mt-6 rounded-3xl border border-[#EEEFEF] bg-white premium-shadow p-8 md:p-12" data-testid="kb-article-reader">
              <button onClick={() => go(`${basePath}/${gid}/${cid}`)} data-testid="kb-article-back" className="text-sm text-[#723FFF] hover:text-[#5b2fd6] flex items-center gap-1 mb-6"><ChevronLeft className="h-4 w-4" /> {cat.name}</button>
              <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-[#06060C] leading-tight break-words" data-testid="kb-article-title">{article.title}</h2>
              <div className="mt-8 kb-article prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: article.body || "<p style='color:#9FA1A4'>No content yet.</p>" }} />
            </article>
          )}
        </>
      )}
    </div>
  );
}
